/**
 * Runtime configuration loaded from /config.json before React mounts.
 *
 * The CDK ``UiStack`` rewrites this file per-stage via ``BucketDeployment``
 * so the same bundled JS works against dev or production Cognito pools
 * without an env-specific build.
 */

export interface RuntimeConfig {
  apiBaseUrl: string;
  /** Nom de l'environnement du control plane ("production", "dev", …). */
  stage?: string;
  /**
   * Vrai sur le control plane de production.
   *
   * Sert à ne pas proposer des actions que l'API de cet environnement
   * n'expose pas — la rotation des identifiants, par exemple, dont les routes
   * n'existent qu'en production. C'est du confort d'affichage : la garde
   * réelle est l'absence de la route, pas ce drapeau. D'où le défaut à faux
   * quand le champ manque (config.json antérieur à son introduction) : au
   * pire un bouton reste caché jusqu'au prochain déploiement de l'UI.
   */
  isProduction?: boolean;
  cognito: {
    region: string;
    userPoolId: string;
    userPoolClientId: string;
    /** Cognito Hosted UI domain (https://<prefix>.auth.<region>.amazoncognito.com) */
    domain: string;
  };
}

/** Raccourci lisible : le control plane courant autorise-t-il les écritures ? */
export function isProductionPlane(): boolean {
  return getRuntimeConfig().isProduction === true;
}

let cached: RuntimeConfig | null = null;

export async function loadRuntimeConfig(): Promise<RuntimeConfig> {
  if (cached) return cached;
  const response = await fetch("/config.json", { cache: "no-store" });
  if (!response.ok) {
    throw new Error(`Failed to load /config.json: HTTP ${response.status}`);
  }
  cached = (await response.json()) as RuntimeConfig;
  return cached;
}

export function getRuntimeConfig(): RuntimeConfig {
  if (!cached) {
    throw new Error(
      "RuntimeConfig accessed before loadRuntimeConfig() resolved — guard with the bootstrap promise"
    );
  }
  return cached;
}
